import type fr from '../fr/adminCrm';

const en: typeof fr = {
  filters: { allSectors: 'All sectors' },
  form: {
    invalid: 'Invalid value.',
    checkFields: 'Some information is missing or invalid. Please check the form.',
  },
  picker: {
    searchClient: 'Search a client (name, email, phone, address)…',
    results: 'Matching clients',
    noClient: 'No client found.',
    change: 'Change',
  },
  errors: {
    TOTAL_MUST_BE_POSITIVE: 'The total must be greater than $0.',
    NO_PRICE: 'Save a price version first.',
    QUOTE_WITHOUT_DATE: 'The quote has no requested date: the cleaning cannot be created.',
    QUOTE_NOT_FOUND: 'Quote not found.',
    CLIENT_REQUIRED: 'Choose a client.',
    CUSTOM_INTERVAL_REQUIRED: 'Enter the interval in days.',
    NOT_YOUR_PROPERTY: 'This property does not belong to this client.',
    SERVICE_TYPE_NOT_FOUND: 'Service type not found.',
    AGREEMENT_NOT_FOUND: 'Recurring service not found.',
    AGREEMENT_NOT_ACTIVE: 'This service is not active.',
    AGREEMENT_NOT_PAUSED: 'This service is not paused.',
    AGREEMENT_CLOSED: 'This service is already ended or cancelled.',
    USE_CANCEL_INSTEAD: 'To stop the visits, cancel the service instead.',
    STAFF_ONLY_ACTION: 'Staff-only action.',
    ONE_COUNTERPART_ONLY: 'Choose a single recipient.',
    JOB_NOT_FOUND: 'Cleaning not found.',
  },

  quotes: {
    title: 'Quotes',
    description: 'Client price requests: review, official price, sending and response.',
    search: 'Client, city or quote #',
    tabs: { todo: 'To handle', sent: 'Sent', accepted: 'Accepted', closed: 'Rejected / expired', all: 'All' },
    empty: {
      todo: 'No quote to handle. You are all caught up.',
      sent: 'No quote waiting for a client answer.',
      accepted: 'No accepted quote yet.',
      closed: 'No rejected or expired quote.',
      all: 'No quotes yet.',
    },
    emptySearch: 'No quote matches this search.',
    estimateShort: 'est. {{amount}}',
    estimateInternal: 'Internal estimate (no official price yet)',
    fields: {
      property: 'Property',
      service: 'Service',
      frequency: 'Service frequency',
      frequencyHint: 'Visit rhythm. Billing is chosen separately, when proposing the price.',
      customIntervalDays: 'Interval (days)',
      contractTerm: 'Contract',
      contractOnlyRecurring: 'A contract only applies to a recurring service.',
      requested: 'Requested date',
      requestedDate: 'Requested date',
      requestedTime: 'Requested time',
      extras: 'Extras',
      specialRequests: 'Special requests',
      received: 'Received',
    },
    new: {
      button: 'New quote',
      title: 'New quote',
      description: 'For a request received by phone or in person. The internal estimate is computed automatically.',
      noProperty: 'This client has no active property.',
      addProperty: 'Add a property',
      submit: 'Create the quote',
      created: 'Quote created.',
    },
  },

  quote: {
    title: 'Quote #{{number}}',
    subtitle: 'Received on {{date}}',
    request: 'Client request',
    snapshot: 'Property at the time of the request',
    snapshotHint: 'These characteristics were used for the estimate. Later property changes do not affect them.',
    snap: {
      bedrooms_one: '{{count}} bedroom',
      bedrooms_other: '{{count}} bedrooms',
      bathrooms_one: '{{count}} bathroom',
      bathrooms_other: '{{count}} bathrooms',
      kitchens_one: '{{count}} kitchen',
      kitchens_other: '{{count}} kitchens',
      familyRooms_one: '{{count}} family room',
      familyRooms_other: '{{count}} family rooms',
      floors_one: '{{count}} floor',
      floors_other: '{{count}} floors',
      sqft_one: '{{count}} sq ft',
      sqft_other: '{{count}} sq ft',
      pets: 'Pets',
    },
    everyDays_one: 'Every day',
    everyDays_other: 'Every {{count}} days',
    timeline: {
      submitted: 'Request received: {{date}}',
      sent: 'Sent to client: {{date}}',
      responded: 'Client answer: {{date}}',
      duration_one: 'Planned duration: {{duration}} · {{count}} person',
      duration_other: 'Planned duration: {{duration}} · {{count}} people',
    },
    clientNote: 'Client note',
    takeOver: 'Take over',
    reviewTaken: 'You are handling this quote.',
    reopen: 'Reopen review',
    reopened: 'Quote reopened.',
    send: 'Send to client',
    sendVersion: 'Send V{{version}} to client',
    sentToast: 'Quote sent to the client.',
    acceptForClient: 'Accept for the client',
    rejectForClient: 'Reject for the client',
    confirmAccept: {
      title: 'Accept on behalf of the client?',
      description: 'Version V{{version}} ({{amount}}) will be accepted. The cleaning (or recurring service) will be created and the client will receive a confirmation.',
    },
    confirmReject: {
      title: 'Reject on behalf of the client?',
      description: 'The quote will be marked as rejected. You can reopen it later.',
    },
    responseNote: 'Note (optional)',
    responseNoteHint: 'E.g. “Accepted by phone on October 2”.',
    acceptedToast: 'Quote accepted.',
    acceptedWithJob: 'Quote accepted. The first cleaning is created and waiting to be assigned.',
    rejectedToast: 'Quote rejected.',
    cancel: 'Cancel the quote',
    confirmCancel: {
      title: 'Cancel quote #{{number}}?',
      description: 'The quote can no longer be changed or accepted. The version history is kept.',
    },
    cancelledToast: 'Quote cancelled.',
    messageClient: 'Message the client',
    conversationSubject: 'Quote #{{number}}',
    notEditable: 'The price of this quote can no longer be changed.',
    acceptedBanner: 'Accepted: V{{version}} at {{amount}}.',
    seeAgreement: 'See the recurring service',
    seeJobs: 'See the client’s cleanings',
  },
  estimate: {
    title: 'Internal estimate',
    description: 'Automatic calculation from the characteristics. Never shown to the client: only the official price is sent.',
    none: 'No estimate available for this request.',
    total: 'Estimated total',
    duration_one: '{{duration}} · {{count}} person recommended',
    duration_other: '{{duration}} · {{count}} people recommended',
  },
  pricing: {
    title: 'Official price',
    description: 'Each save creates a new version (V{{version}}). Previous versions stay in the history.',
    fromEstimate: 'Start from the estimate',
    fromVersion: 'Start from V{{version}}',
    roundingLine: 'Rounding / minimum',
    line: {
      label: 'Description',
      labelPlaceholder: 'E.g. Standard cleaning, Inside oven…',
      quantity: 'Qty',
      unit: 'Unit price ($)',
      total: 'Total',
      add: 'Add a line',
      remove: 'Remove the line',
      discountHint: 'For a discount, enter a negative unit price (e.g. -15).',
    },
    totalNew: 'Total V{{version}}',
    compare: 'V{{from}} {{fromAmount}} → V{{to}} {{toAmount}}',
    estimatedMinutes: 'Planned duration (minutes)',
    requiredWorkers: 'People required',
    billing: 'Billing',
    billingHint: 'Independent from the service frequency ({{frequency}}).',
    messageToClient: 'Message to the client',
    messageHint: 'Visible to the client with the price.',
    internalNote: 'Internal note',
    internalNoteHint: 'Visible to staff only.',
    validUntil: 'Valid until',
    validUntilHint: 'After this date, the quote expires.',
    invalid: 'Check the lines: description, quantity (whole number ≥ 1) and unit price are required. Duration is at least 15 minutes.',
    replacesSent: 'This quote was already sent: a new version puts it back in proposal until it is sent again.',
    saveVersion: 'Save version V{{version}}',
    saveAndSend: 'Save and send to client',
    needsApproval: 'Sending to the client requires the price approval permission.',
    savedToast: 'Version V{{version}} saved.',
    sentToast: 'Version V{{version}} ({{amount}}) sent to the client.',
    confirmSend: {
      title: 'Send version V{{version}} to the client?',
      description: '{{name}} will receive the price of {{amount}} and can accept or reject it.',
    },
  },
  versions: {
    title: 'Version history',
    none: 'No price proposed yet.',
    by: 'By {{author}} · {{date}}',
    current: 'Current version',
    sent: 'Sent',
    accepted: 'Accepted',
    workers_one: '{{count}} person',
    workers_other: '{{count}} people',
    discount: 'discount {{amount}}',
    showLines_one: 'Show the line',
    showLines_other: 'Show the {{count}} lines',
  },

  clients: {
    title: 'Clients',
    count_one: '{{count}} client',
    count_other: '{{count}} clients',
    search: 'Name, email, phone or address',
    showArchived: 'Archived clients',
    empty: 'No clients yet.',
    emptyArchived: 'No archived client.',
    emptySearch: 'No client matches this search.',
    recurring: 'Recurring',
    occasional: 'One-time',
    archived: 'Archived',
    contact: 'Contact',
    properties_one: '{{count}} property',
    properties_other: '{{count}} properties',
    propertiesShort: 'Properties',
    type: 'Type',
    nextJob: 'Next cleaning',
    noNextJob: 'None planned',
  },
  client: {
    new: 'New client',
    newHint: 'The client can create an online account later with the same email.',
    create: 'Create the client',
    created: 'Client created.',
    edit: 'Edit the client',
    saved: 'Client updated.',
    since: 'Client since {{date}}',
    profile: 'Profile',
    account: 'Online account',
    noAccount: 'No account (managed by staff)',
    accountNeverUsed: 'Account created, never used',
    lastLogin: 'Last login: {{date}}',
    fields: {
      displayName: 'Name',
      secondaryPhone: 'Other phone',
      source: 'Source',
      sourceHint: 'E.g. referral, Facebook, website.',
      internalNotes: 'Internal notes',
      internalNotesHint: 'Never visible to the client.',
    },
    archive: 'Archive the client',
    restore: 'Restore the client',
    archivedToast: 'Client archived.',
    restoredToast: 'Client restored.',
    confirmArchive: {
      title: 'Archive {{name}}?',
      description: 'The client will disappear from lists and searches. Cleanings, quotes and payments are kept. You can restore the client later.',
    },
    actions: { newJob: 'Create a cleaning', newQuote: 'Create a quote', message: 'Message' },
    tabs: {
      properties: 'Properties',
      jobs: 'Cleanings',
      agreements: 'Recurring services',
      quotes: 'Quotes',
      payments: 'Payments',
      conversations: 'Conversations',
    },
    upcoming: 'Upcoming cleanings',
    history: 'History',
    noUpcoming: 'No cleaning planned.',
    noHistory: 'No past cleaning.',
    noProperty: 'This client has no property yet.',
    noQuote: 'No quote for this client.',
    quoteCreated: 'created on {{date}}',
    noPayment: 'No payment.',
    payment: 'Payment',
    paidOn: 'paid on {{date}}',
    refunded: 'refunded {{amount}}',
    noConversation: 'No conversation with this client.',
  },
  agreements: {
    empty: 'No recurring service.',
    emptyHint: 'A recurring service is created when the client accepts a weekly, biweekly or monthly quote.',
    actions: 'Service actions',
    pricePerVisit: 'Price per visit',
    period: 'Period',
    noEnd: 'no end',
    pausedFrom: 'Paused since {{from}}',
    pausedRange: 'Paused from {{from}} to {{to}}',
    action: {
      PAUSE: 'Pause',
      RESUME: 'Resume the service',
      CANCEL: 'Cancel the service',
      CHANGE_SCHEDULE: 'Change the schedule',
      CHANGE_PRICE: 'Change the price',
    },
    hint: {
      PAUSE: 'Cleanings planned during the pause are cancelled. Without an end date, the pause lasts until resumed.',
      RESUME: '',
      CANCEL: '',
      CHANGE_SCHEDULE: 'Visits of the old rhythm from this date are cancelled (except manually modified ones) and new ones are generated.',
      CHANGE_PRICE: 'The new price applies to the next generated visits.',
    },
    confirm: {
      RESUME: { title: 'Resume the service?', description: 'Next visits will be generated from today.' },
      CANCEL: { title: 'Cancel this recurring service?', description: 'All future visits not started yet will be cancelled. No penalty is applied.' },
      PAUSE: { title: '', description: '' },
      CHANGE_SCHEDULE: { title: '', description: '' },
      CHANGE_PRICE: { title: '', description: '' },
    },
    pauseFrom: 'Pause from',
    pauseUntil: 'Until',
    pauseUntilHint: 'Optional.',
    startDate: 'Starting on',
    newPrice: 'New price per visit ($)',
    currentPrice: 'Current price: {{amount}}',
    applyFuture: 'Also apply to cleanings already planned',
    reason: 'Reason',
    done: {
      PAUSE: 'Service paused.',
      RESUME: 'Service resumed.',
      CANCEL: 'Service cancelled.',
      CHANGE_SCHEDULE: 'Schedule changed.',
      CHANGE_PRICE: 'Price changed.',
    },
    result: {
      cancelled_one: '{{count}} cleaning cancelled',
      cancelled_other: '{{count}} cleanings cancelled',
      generated_one: '{{count}} cleaning created',
      generated_other: '{{count}} cleanings created',
      updated_one: '{{count}} cleaning updated',
      updated_other: '{{count}} cleanings updated',
    },
  },
  jobs: {
    new: {
      title: 'Create a cleaning',
      description: 'One-time cleaning for {{name}}, without a quote.',
      noProperty: 'Add a property to this client first.',
      price: 'Client price ($)',
      priority: 'Priority',
      specialInstructions: 'Instructions for the team',
      specialInstructionsHint: 'Visible to assigned employees.',
      notes: 'Internal notes',
      notesHint: 'Visible to the office only.',
      submit: 'Create the cleaning',
      created: 'Cleaning created. It still needs to be assigned.',
    },
  },

  properties: {
    title: 'Properties',
    description: 'All active client addresses.',
    count_one: '{{count}} active property',
    count_other: '{{count}} active properties',
    search: 'Name, address or city',
    empty: 'No active property.',
    emptySearch: 'No property matches this search.',
    emptyHint: 'Properties are added from a client’s page.',
    noSector: 'No sector',
    openClient: 'See the client',
  },
  property: {
    add: 'Add a property',
    addHint: 'New address for {{name}}. The sector is detected automatically from the address.',
    created: 'Property added.',
    edit: 'Edit the property',
    saved: 'Property updated.',
    inactive: 'Inactive',
    unitShort: 'unit {{unit}}',
    seeClient: 'See the client',
    permanentInfo: 'Permanent information',
    upcomingJobs: 'Upcoming cleanings at this address',
    noUpcoming: 'No cleaning planned at this address.',
    sections: { address: 'Address', characteristics: 'Characteristics', onSite: 'On site' },
    fields: {
      name: 'Property name',
      nameHint: 'E.g. House, Cottage, Downtown condo.',
      propertyType: 'Type',
      addressLine1: 'Address',
      unit: 'Apartment / unit',
      city: 'City',
      province: 'Province',
      postalCode: 'Postal code',
      sectorHint: 'Keep “Automatic detection” to use the address location.',
      sectorAuto: 'Automatic detection',
      floors: 'Floors',
      bedrooms: 'Bedrooms',
      bathrooms: 'Bathrooms',
      kitchens: 'Kitchens',
      familyRooms: 'Family rooms',
      squareFeet: 'Area (sq ft)',
      hasPets: 'Pets on site',
      petDetails: 'Pet details',
      parkingInfo: 'Parking',
      permanentNotes: 'Permanent notes (visible to the assigned team)',
      preferences: 'Client preferences',
      specialProducts: 'Special products',
      forbiddenAreas: 'Forbidden areas',
    },
  },
  notes: {
    title: 'Notes',
    description: 'Each note has a visibility: internal (office), assigned employees or client.',
    placeholder: 'Add a note…',
    visibility: 'Visibility',
    vis: { INTERNAL: 'Internal', EMPLOYEE: 'Employees', CLIENT: 'Client' },
    visHint: {
      INTERNAL: 'Visible to the office only.',
      EMPLOYEE: 'Visible to the office and employees assigned to this address.',
      CLIENT: 'Visible to the office, employees and the client.',
    },
    add: 'Add',
    added: 'Note added.',
    deleted: 'Note deleted.',
    empty: 'No notes.',
    confirmDelete: 'Delete this note?',
  },
  access: {
    title: 'Property access',
    audited: 'Sensitive information (codes, keys, alarm). Every view is recorded in the audit log with your name.',
    show: 'Show access',
    hide: 'Hide',
    empty: 'No access instructions.',
    add: 'Add access',
    kind: 'Type',
    label: 'Description',
    labelPlaceholder: 'E.g. Front door, Garage lockbox',
    secret: 'Code / value',
    notes: 'Details',
    saved: 'Access saved.',
    deleted: 'Access deleted.',
    confirmDelete: 'Delete this access instruction?',
  },
  checklist: {
    title: 'Task list adjustments',
    description: 'Tasks added or removed for this property, applied to every new cleaning.',
    empty: 'No adjustment: the standard service list applies.',
    addedIn: 'Added · {{section}}',
    removedFromTemplate: 'Removed from the standard list',
    unknownItem: 'Standard list task',
    added: 'Task added.',
    removed: 'Task removed',
    deleted: 'Adjustment deleted.',
    section: 'Section',
    taskLabel: 'Task',
    placeholder: 'E.g. Descale the main shower',
    add: 'Add the task',
    remove: 'Delete the adjustment',
    confirmDelete: 'Delete this adjustment?',
    confirmDeleteHint: 'Next cleanings will use the standard list for this task. Cleanings already created do not change.',
  },

  messages: {
    title: 'Messages',
    description: 'Conversations with clients and employees.',
    select: 'Choose a conversation.',
    empty: 'No conversations yet.',
    emptyFiltered: 'No conversation with these filters.',
    filters: {
      label: 'Filter conversations',
      all: 'All',
      mine: 'Mine',
      unassigned: 'Unassigned',
      open: 'Open',
      waiting: 'Waiting',
      resolved: 'Resolved',
      kind: 'Conversation type',
      allKinds: 'Clients and employees',
      clients: 'Clients',
      employees: 'Employees',
    },
    kind: { client: 'Client', employee: 'Employee', internal: 'Internal' },
    panel: {
      related: 'Links',
      job: 'Related cleaning',
      quote: 'Related quote',
      property: 'Related property',
      priority: 'Priority',
      assignee: 'Assignee',
      assignMe: 'Assign to me',
      you: 'You',
      assigned: 'Assigned',
      noAccount: 'This client has no online account: they will not see the messages.',
      mentionHint: 'In an internal note, type @FirstName to notify a colleague.',
    },
    new: {
      title: 'New message',
      description: 'Start a conversation with a client or an employee.',
      recipientKind: 'Recipient',
      chooseEmployee: 'Choose an employee',
      to: 'To:',
    },
  },
};
export default en;
